import { queryOptions, type QueryClient } from "@tanstack/react-query";

import { MAINTENANCE_STATUS_PATH, isMaintenanceModeEnabled } from "@/lib/maintenance";

/**
 * How long a tab trusts its last answer before asking again. This also caps
 * how long an open tab can disagree with the server after the flag flips.
 * Intent preloads re-check on hover, so a click rarely waits on this.
 */
export const MAINTENANCE_STATUS_STALE_TIME_MS = 30_000;

/**
 * How often an open tab re-checks *while the window is open*. Covers the tab
 * that sits focused and idle on the landing page, which never fires a focus
 * event and so would otherwise keep its banner and disabled CTAs until the
 * user navigated or reloaded. Off entirely once the window closes, so normal
 * operation does no polling.
 */
export const MAINTENANCE_STATUS_POLL_MS = 60_000;

export type MaintenanceStatus = {
  maintenance: boolean;
};

export function maintenanceStatusQueryKey() {
  return ["maintenance", "status"] as const;
}

export async function fetchMaintenanceStatus(
  fetchImpl: typeof fetch = fetch,
): Promise<MaintenanceStatus> {
  const response = await fetchImpl(MAINTENANCE_STATUS_PATH, {
    cache: "no-store",
    headers: { accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`Maintenance status check failed with HTTP ${response.status}`);
  }

  const body: unknown = await response.json();
  if (
    typeof body !== "object" ||
    body === null ||
    typeof (body as { maintenance?: unknown }).maintenance !== "boolean"
  ) {
    throw new Error("Maintenance status response was malformed");
  }

  return { maintenance: (body as MaintenanceStatus).maintenance };
}

/**
 * On the server the answer is local, with no HTTP round trip to ourselves.
 * In the browser, always ask the server: the browser's own copy of the env
 * var is whatever Vite baked in at build time.
 */
export function readMaintenanceStatus(): Promise<MaintenanceStatus> {
  if (typeof window === "undefined") {
    return Promise.resolve({ maintenance: isMaintenanceModeEnabled() });
  }

  return fetchMaintenanceStatus();
}

export function maintenanceStatusQueryOptions() {
  return queryOptions({
    queryKey: maintenanceStatusQueryKey(),
    queryFn: readMaintenanceStatus,
    staleTime: MAINTENANCE_STATUS_STALE_TIME_MS,
    // Overrides the app-wide default: a tab left open through the window
    // should drop the banner and re-enable its CTAs when the user returns.
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      query.state.data?.maintenance === true ? MAINTENANCE_STATUS_POLL_MS : false,
    // A navigation is waiting on this; don't make it wait through retries.
    retry: false,
  });
}

/**
 * Resolve the flag for a navigation. Never throws. If the check fails, use the
 * last known answer; with no answer at all, let the navigation through. The
 * server entry still turns away every document request while the window is
 * open, so failing open here cannot put anyone on a dead page after a reload.
 */
export async function resolveMaintenanceMode(queryClient: QueryClient): Promise<boolean> {
  try {
    const status = await queryClient.fetchQuery(maintenanceStatusQueryOptions());
    return status.maintenance;
  } catch {
    return (
      queryClient.getQueryData<MaintenanceStatus>(maintenanceStatusQueryKey())?.maintenance ?? false
    );
  }
}
